<template>
  <div class="results-date-selector" :class="classModifiers">
    <div class="results-date-selector__week">
      <sp-button
        v-if="grid.previous"
        class="results-date-selector__arrow --previous"
        variant="text"
        icon="chevron-left-heavy"
        densitysmcompact="false"
        :label="resultText('show_previous_dates')"
        :data-testid="TestId.finder.dateRowPrevious"
        @click="showPreviousDates"
      ></sp-button>
      <div ref="dates" class="results-date-selector__dates">
        <div v-for="day in days" :key="day.date" class="results-date-selector__date">
          <span class="results-date-selector__weekday">{{ day.weekday }},</span>
          <span class="results-date-selector__day">{{ day.label }}</span>
        </div>
      </div>
      <sp-button
        v-if="grid.next"
        class="results-date-selector__arrow --next"
        variant="text"
        icon="chevron-right-heavy"
        densitysmcompact="false"
        :label="resultText('show_next_dates')"
        :data-testid="TestId.finder.dateRowNext"
        @click="showNextDates"
      ></sp-button>
    </div>
    <ResultsGoToDate
      v-if="goToDate"
      ref="goToDateControl"
      class="results-date-selector__go-to-date"
      :start-date="startDate"
      :request-key="requestKey"
      :availability="availability"
      :mobile="mobile"
      :divided="listLayout"
      @select-date="selectDate"
    />
  </div>
</template>

<script setup>
import { TestId } from "#/test-ids";
import { computed, useTemplateRef, watch } from "vue";
import { parseISO } from "date-fns";
import { formatDatePattern } from "#/shared/lib/helpers/localized-date";
import { resultText } from "#ce/components/SpFinder/view-models/result-text";
import { trackControlClick } from "#ce/components/SpFinder/lib/click-analytics";
import ResultsGoToDate from "./ResultsGoToDate.ce.vue";

const SLIDE_DISTANCE = 100;
const FADE_DELAY = 500;
const FADE_DURATION = 400;

const props = defineProps({
  grid: {
    type: Object,
    required: true,
  },
  startDate: {
    type: String,
    default: undefined,
  },
  requestKey: {
    type: String,
    default: undefined,
  },
  showYear: {
    type: String,
    default: undefined,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  /** Names the weekday and month in full on one line, at the mobile layout's size. */
  longDates: {
    type: Boolean,
    default: false,
  },
  goToDate: {
    type: Boolean,
    default: false,
  },
  listLayout: {
    type: Boolean,
    default: false,
  },
  availability: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(["select-date"]);

const datesElement = useTemplateRef("dates");
const goToDateControl = useTemplateRef("goToDateControl");

function dayEntry(date) {
  const parsed = parseISO(date);
  const short = !props.mobile && !props.longDates;
  return {
    date,
    weekday: formatDatePattern(parsed, short ? "EEE" : "EEEE"),
    label: formatDatePattern(
      parsed,
      short ? "MMM d" : "MMMM d",
      short ? "MMM d, yyyy" : "MMMM d, yyyy",
      props.showYear,
    ),
  };
}

const days = computed(() => (props.grid.dates ?? []).map(dayEntry));
const classModifiers = computed(() => ({ "--mobile": props.mobile, "--long-dates": props.longDates && !props.mobile }));

function selectDate(date) {
  if (props.goToDate) {
    datesElement.value?.animate([{ opacity: 1 }, { opacity: 0 }], {
      delay: FADE_DELAY,
      duration: FADE_DURATION,
      fill: "forwards",
    });
    goToDateControl.value?.hide();
  }
  emit("select-date", date);
}

function showPreviousDates(event) {
  trackControlClick(event);
  selectDate(props.grid.previous);
}

function showNextDates(event) {
  trackControlClick(event);
  selectDate(props.grid.next);
}

// A finder that opened on the default start date has no previous one; its dates fade in without sliding.
function slideIn(startDate, previousStartDate) {
  if (!props.goToDate || !datesElement.value) {
    return;
  }
  let offset = 0;
  if (previousStartDate && startDate !== previousStartDate) {
    offset = startDate > previousStartDate ? SLIDE_DISTANCE : -SLIDE_DISTANCE;
  }
  datesElement.value.getAnimations().forEach((animation) => animation.cancel());
  datesElement.value.animate(
    [
      { opacity: 0, position: "relative", left: `${offset}px` },
      { opacity: 1, position: "relative", left: "0px" },
    ],
    { duration: FADE_DURATION },
  );
}

watch(() => props.startDate, slideIn, { flush: "post" });
</script>

<style lang="scss" scoped>
.results-date-selector {
  --color: var(--sp-comp-finder-results-date-selector-color, #4a4a4a);
  --arrow-color: var(--sp-comp-finder-results-date-selector-arrow-color, var(--sp-sys-color-primary));
  --font-size: var(--sp-comp-finder-results-date-selector-font-size, 16px);
  --font-family: var(--sp-comp-finder-results-date-selector-font-family, inherit);
  --font-weight: var(--sp-comp-finder-results-date-selector-font-weight, bold);
  --arrow-inset: var(--sp-comp-finder-results-date-selector-arrow-inset, 0);
  // At a 32px icon size the chevron glyph is the legacy 18px font glyph's size, so the box is centred where that
  // glyph's box sat.
  --arrow-centring: calc((var(--sp-ref-scale-18) - var(--sp-ref-scale-32)) / 2);
  --arrow-inset-mobile: var(--sp-comp-finder-results-date-selector-arrow-inset-mobile, 10px);
  --mobile-padding-block: var(--sp-comp-finder-results-date-selector-padding-block-mobile, 17px);
  --mobile-gap: var(--sp-comp-finder-results-date-selector-gap-mobile, 4px);
  --mobile-font-size: var(--sp-comp-finder-results-date-selector-font-size-mobile, 18px);

  position: relative;
  height: var(--sp-comp-finder-results-date-selector-height, 100%);
  border-bottom: var(--sp-comp-finder-results-date-selector-border-bottom, none);
  background: var(--sp-comp-finder-results-date-selector-background, transparent);
  box-shadow: var(--sp-comp-finder-results-date-selector-box-shadow, none);
  color: var(--color);
  font-family: var(--font-family);
  font-size: var(--font-size);
  user-select: none;

  &__week {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__dates {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  &__date {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    width: calc(20% - 5px);
    line-height: 1;
    text-align: center;
  }

  &__weekday {
    font-size: 0.8em;
    font-weight: var(--font-weight);
    line-height: 17px;
  }

  &__day {
    margin-bottom: 2px;
    font-size: var(--font-size);
    font-weight: var(--font-weight);
    line-height: 20px;
  }

  &__arrow {
    --sp-comp-button-primary-variant-text-text-color: var(--arrow-color);
    --sp-comp-button-primary-hover-variant-text-bg-opacity: 0;
    --sp-comp-button-size: var(--sp-ref-scale-32);
    --sp-comp-button-min-width: var(--sp-ref-scale-32);
    --sp-comp-button-padding: 0;
    --sp-comp-icon-size: var(--sp-ref-scale-32);
    --sp-comp-icon-fill: var(--arrow-color);

    position: absolute;
    top: 50%;
    z-index: 1;
    margin-inline: var(--arrow-centring);
    transform: translateY(-50%);

    &.--previous {
      left: var(--arrow-inset);
    }

    &.--next {
      right: var(--arrow-inset);
    }
  }

  // A quirks-mode embed page resolves `height: 100%` against the viewport, so the list row sizes to its content.
  &.--mobile {
    display: grid;
    grid-template-columns: 1fr;
    height: auto;
    row-gap: var(--mobile-gap);
    padding: var(--mobile-padding-block) 0;
    border-block-end: var(
      --sp-comp-finder-results-date-selector-divider-mobile,
      var(--sp-sys-border-width-default) solid #e5e5e5
    );
  }

  &.--mobile &__week {
    overflow: visible;
  }

  &.--mobile &__date {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: auto;
  }

  &.--mobile &__weekday,
  &.--mobile &__day {
    font-size: var(--mobile-font-size);
    line-height: 1;
  }

  &.--mobile &__weekday {
    margin-right: var(--sp-ref-scale-4);
  }

  &.--mobile &__day {
    margin-bottom: 0;
    margin-left: 5px;
  }

  &.--mobile &__arrow.--previous {
    left: var(--arrow-inset-mobile);
  }

  &.--mobile &__arrow.--next {
    right: var(--arrow-inset-mobile);
  }

  &.--long-dates &__date {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  &.--long-dates &__weekday,
  &.--long-dates &__day {
    font-size: var(--mobile-font-size);
    line-height: 1;
  }

  &.--long-dates &__weekday {
    margin-right: var(--sp-ref-scale-4);
  }

  &.--long-dates &__day {
    margin-bottom: 0;
  }

  &:not(.--mobile) &__go-to-date {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translate3d(-50%, 50%, 0);
  }
}
</style>
